import { useState } from 'react';

/**
 * PDPAccordionSection — generic collapsible accordion wrapper for PDP content sections.
 *
 * Each section manages its own expanded/collapsed state independently,
 * allowing multiple sections to be open simultaneously.
 *
 * Uses CSS grid-template-rows animation for smooth expand/collapse transitions.
 */

function slugify(text) {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
}

export default function PDPAccordionSection({
  title,
  defaultExpanded = false,
  children,
  className,
}) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const sectionId = `pdp-section-${slugify(title)}`;
  const headerId = `pdp-header-${slugify(title)}`;

  return (
    <div className={`border-b border-n2 ${className || ''}`}>
      <button
        id={headerId}
        className="w-full flex justify-between items-center py-5 px-4 bg-n1 hover:bg-n2 transition-colors cursor-pointer text-left"
        onClick={() => setIsExpanded((prev) => !prev)}
        aria-expanded={isExpanded}
        aria-controls={sectionId}
      >
        <h2 className="text-lg font-normal text-carbon-black font-sandvik-headline">
          {title}
        </h2>
        <span className="text-2xl text-n6 font-light select-none">
          {isExpanded ? '−' : '+'}
        </span>
      </button>

      <div
        id={sectionId}
        role="region"
        aria-labelledby={headerId}
        className="grid transition-all duration-300 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          {children}
        </div>
      </div>
    </div>
  );
}
